import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";
import {
    getParamsFromFacetValues,
    updateQueryParams,
} from "tsi-common-react/src/apps/reviews/history";
import {
    setFacetValues,
    setProductTypes,
} from "tsi-common-react/src/apps/reviews/reducers";
import { loadReviews } from "tsi-common-react/src/apps/reviews/thunks";
import {
    IReviewsBrandID,
    IReviewsProductTypeID,
} from "tsi-common-react/src/models/nominals";
import {
    IReviewQuery,
    IReviewsProduct,
    ISearchFacet,
    ISearchReviewArgs,
    ReviewSortOption,
} from "tsi-common-react/src/models/reviews";
import {
    useAppDispatch,
    useAppSelector,
} from "tsi-common-react/src/store/reducers";
import { notEmpty, unique } from "tsi-common-react/src/utils/functional";
import { serializeFacetValues } from "tsi-common-react/src/utils/reviews";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { ReviewsViewer as ReviewsViewerComponent } from "./ReviewsViewerContent";

type IProps = Immutable<{
    brandID: IReviewsBrandID;
    pageSize?: number;
    displayedProductTypeIDWhitelist?: IReviewsProductTypeID[];
    sourceFilterWhitelist?: string[];
    starHasStroke?: boolean;
}>;

const computeProductTypeIDs = (
    products: ReadonlyArray<IReviewsProduct>,
): readonly IReviewsProductTypeID[] => {
    return products
        .map((p) => p.product_type)
        .filter(notEmpty)
        .filter(unique);
};

const buildReviewSearchQuery = (
    brandID: IReviewsBrandID,
    selectedSortOption: ReviewSortOption,
    productTypeIDs: readonly IReviewsProductTypeID[],
    facetValues: IReviewQuery,
    page: number,
    pageSize: number,
    displayedProductTypeIDWhitelist?: readonly IReviewsProductTypeID[],
): ISearchReviewArgs => {
    let effectiveProductTypeIDs = productTypeIDs;
    if (
        effectiveProductTypeIDs.length <= 0 &&
        displayedProductTypeIDWhitelist &&
        displayedProductTypeIDWhitelist.length > 0
    ) {
        effectiveProductTypeIDs = displayedProductTypeIDWhitelist;
    }
    return {
        brand_id: brandID,
        ordering: selectedSortOption,
        page: page,
        page_size: pageSize,
        ...facetValues,
        product_type_id: serializeFacetValues(effectiveProductTypeIDs),
    };
};

const initSourceFilter = (
    facets: ReadonlyArray<ISearchFacet>,
    facetValues: IReviewQuery,
    sourceFilterWhitelist: readonly string[] | undefined,
    dispatch: ReturnType<typeof useAppDispatch>,
): void => {
    if (!sourceFilterWhitelist || sourceFilterWhitelist.length <= 0) {
        return;
    }
    const sourceFacet = facets.find((facet) => {
        return facet.type === "source_id";
    });
    if (!sourceFacet) {
        return;
    }
    const optionIDs = sourceFacet.options
        .filter((option) => {
            return sourceFilterWhitelist.includes(`${option.name}`);
        })
        .map((option) => {
            return option.option_id;
        });
    const sourceIDs = serializeFacetValues(optionIDs);
    if (facetValues.source_id !== sourceIDs) {
        dispatch(
            setFacetValues({
                ...facetValues,
                source_id: sourceIDs,
            }),
        );
    }
};

export const ReviewsViewer = (props: IProps): React.ReactNode => {
    const pageSize = props.pageSize ?? 6;

    const dispatch = useAppDispatch();

    const products = useAppSelector((state) => {
        return state.reviews.data.products;
    });
    const productTypeIDs = useAppSelector((state) => {
        return state.reviews.ui.productTypeIDs;
    });
    const facets = useAppSelector((state) => {
        return state.reviews.data.facets;
    });
    const facetValues = useAppSelector((state) => {
        return state.reviews.ui.facetValues;
    });
    const selectedSortOption = useAppSelector((state) => {
        return state.reviews.ui.selectedSortOption;
    });
    const page = useAppSelector((state) => {
        return state.reviews.ui.page;
    });
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const hasMounted = useRef(false);
    const listReviewsTimerRef = useRef<number | undefined>(undefined);

    const listReviews = async (
        pageNum: number,
        _appendToExisting: boolean,
    ): Promise<void> => {
        const query = buildReviewSearchQuery(
            props.brandID,
            selectedSortOption,
            productTypeIDs,
            facetValues,
            pageNum,
            pageSize,
            props.displayedProductTypeIDWhitelist
                ? props.displayedProductTypeIDWhitelist
                : undefined,
        );
        await dispatch(
            loadReviews({
                brandID: props.brandID,
                query,
            }),
        );
    };

    const listReviewsDebounced = (
        pageNum: number,
        appendToExisting: boolean,
        delayMs = 100,
    ): void => {
        const window = getWindow();
        if (!window) {
            return;
        }
        if (listReviewsTimerRef.current) {
            window.clearTimeout(listReviewsTimerRef.current);
        }
        listReviewsTimerRef.current = window.setTimeout(() => {
            void listReviews(pageNum, appendToExisting);
        }, delayMs);
    };

    const onLoadMore = async (): Promise<void> => {
        setIsLoadingMore(true);
        await listReviews(page + 1, true);
        setIsLoadingMore(false);
    };

    // Update source filter when sourceFilterWhitelist or facets change
    useEffect(() => {
        initSourceFilter(
            facets,
            facetValues,
            props.sourceFilterWhitelist
                ? props.sourceFilterWhitelist
                : undefined,
            dispatch,
        );
    }, [props.sourceFilterWhitelist, facets, facetValues, dispatch]);

    // Reload reviews when sort, filters, or product types change.
    // Skip the initial mount — SSR data is already in the store.
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        listReviewsDebounced(1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSortOption, productTypeIDs, facetValues]);

    // Update product type IDs based on products
    const computedProductTypeIDs = computeProductTypeIDs(products);

    useEffect(() => {
        if (
            JSON.stringify(productTypeIDs) !==
            JSON.stringify(computedProductTypeIDs)
        ) {
            dispatch(setProductTypes(computedProductTypeIDs));
        }
    }, [computedProductTypeIDs, productTypeIDs, dispatch]);

    // Update query string to match current facet selections
    useEffect(() => {
        const params = getParamsFromFacetValues(facetValues, productTypeIDs);
        updateQueryParams(params);
    }, [facetValues, productTypeIDs]);

    return (
        <ReviewsViewerComponent
            onLoadMore={onLoadMore}
            isLoading={isLoadingMore}
        />
    );
};
